<template>
  <div class="container-fluid mt-4" dir="rtl">
    
    <!-- 1. شريط الإحصائيات العلوية -->
    <div class="row mb-4">
      <div class="col-md-6">
        <div class="card bg-danger text-white shadow-sm">
          <div class="card-body d-flex justify-content-between align-items-center px-4">
            <div>
              <h2 class="fw-bold mb-0">{{ stats.pending }}</h2>
              <span class="fs-5">أوراق مطلوب استخراجها الآن</span>
            </div>
            <i class="fas fa-file-import fa-3x opacity-50"></i>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div class="card bg-success text-white shadow-sm">
          <div class="card-body d-flex justify-content-between align-items-center px-4">
            <div>
              <h2 class="fw-bold mb-0">{{ stats.ready_today }}</h2>
              <span class="fs-5">ما تم إنجازه اليوم</span>
            </div>
            <i class="fas fa-check-double fa-3x opacity-50"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. أدوات التحكم والبحث -->
    <div class="card shadow-sm mb-3 border-top-primary">
      <div class="card-body py-3">
        <div class="row g-2 align-items-center">
          
          <!-- بحث برقم الجلوس -->
          <div class="col-md-4">
            <div class="input-group">
              <span class="input-group-text bg-white"><i class="fas fa-search text-primary"></i></span>
              <input 
                v-model="filters.search" 
                @input="debouncedFetch"
                type="text" 
                class="form-control fw-bold" 
                placeholder="ابحث برقم الجلوس..." 
              />
            </div>
          </div>

          <!-- زر التحديث -->
          <div class="col-md-8 text-end">
            <span v-if="loading" class="text-primary me-2 fw-bold small">
              <i class="fas fa-spinner fa-spin"></i> جاري التحديث...
            </span>
            <button @click="fetchData" class="btn btn-dark px-4">
              <i class="fas fa-sync-alt ms-2"></i> تحديث القائمة
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. جدول الطلبات -->
    <div class="card shadow-sm">
      <div class="table-responsive">
        <table class="table table-hover align-middle text-center mb-0">
          <thead class="table-light">
            <tr>
              <th> مجموعة</th>
              <th>سري </th>
              <th>رقم الجلوس</th>
              <th style="width: 20%">اسم الطالب</th>
              <th style="width: 30%">المواد (اضغط للتصوير)</th>
              <th>توقيت الطلب</th>
              <th>توقيت اﻻطلاع</th>
              <th>الحالة</th>
              <th style="width: 15%">الإجراء</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="req in requests" 
              :key="req.id" 
              :class="{'table-success-subtle': req.paper_status === 'ready'}"
            >
              <!-- البيانات الأساسية -->
              <td><span class="badge bg-dark fs-6">{{ req.group_id }}</span></td>
              <td><span class="badge bg-dark fs-6">{{ req.SecretKey }}</span></td>
              <td><span class="badge bg-dark fs-6">{{ req.Seating_No }}</span></td>
              <td class="text-start fw-bold text-primary">{{ req.student_name }}</td>
              
              <!-- المواد (أزرار التصوير) -->
              <td>
                <div class="d-flex flex-wrap justify-content-center gap-2">
                   <button 
                      v-for="sub in req.subjects" 
                      :key="sub.id" 
                      @click="openScanner(sub, req)" 
                      class="btn btn-sm shadow-sm d-flex align-items-center gap-1"
                      :class="sub.paper_status === 'ready' ? 'btn-success' : 'btn-outline-dark'"
                      title="اضغط لتصوير الورقة"
                  >
                      <i v-if="sub.paper_status === 'ready'" class="fas fa-check-circle"></i>
                      <i v-else class="fas fa-camera"></i>
                      {{ sub.subject_name }}
                  </button>
                </div>
              </td>

              <!-- التوقيت -->
              <td class="text-muted small" dir="ltr">{{ formatTime(req.created_at) }}</td>
              <td class="text-muted small" dir="ltr">{{ req.visit_date }}</td>

              <!-- الحالة -->
              <td>
                <span v-if="req.paper_status === 'pending' && !req.add_Subjects" class="badge bg-danger blink-soft">انتظار</span>
                <span v-if="req.status === 'pending' && req.add_Subjects === 'pending' " class="badge bg-danger blink-soft">اضافة مواد</span>
                <span v-if="req.paper_status != 'pending'" class="badge bg-success">جاهز</span>
              </td>

              <!-- الأزرار -->
              <td>
                <button 
                  v-if="req.paper_status === 'pending' || req.add_Subjects === 'pending'"
                  @click="markReady(req)" 
                  class="btn btn-primary btn-sm px-4 fw-bold shadow-sm"
                  :disabled="req.processing"
                >
                  <span v-if="req.processing" class="spinner-border spinner-border-sm"></span>
                  <span v-else><i class="fas fa-check-double me-1"></i> تم الاستخراج</span>
                </button>
                <span v-else class="text-success small fw-bold">
                  <i class="fas fa-user-check"></i> تم التجهيز
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Pagination (الجزء المعدل) -->
      <div class="card-footer bg-white d-flex justify-content-between align-items-center py-3" v-if="pagination.last_page > 1">
          <div class="text-muted small">
              عرض {{ pagination.from }} إلى {{ pagination.to }} من إجمالي {{ pagination.total }} طلب
          </div>
          <nav>
              <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: !pagination.prev_page_url }">
                      <button class="page-link" @click="changePage(pagination.current_page - 1)">
                          <i class="fas fa-chevron-right"></i> السابق
                      </button>
                  </li>
                  <li class="page-item" :class="{ active: page === pagination.current_page }" 
                      v-for="page in getPageNumbers" :key="page">
                      <button class="page-link" @click="changePage(page)">{{ page }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: !pagination.next_page_url }">
                      <button class="page-link" @click="changePage(pagination.current_page + 1)">
                          التالي <i class="fas fa-chevron-left"></i>
                      </button>
                  </li>
              </ul>
          </nav>
      </div>
    </div>

    <!-- نافذة التصوير (Modal) -->
    <div v-if="showScannerModal" class="modal-backdrop fade show"></div>
    <div v-if="showScannerModal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-dark text-white">
            <h5 class="modal-title">
              <i class="fas fa-camera ms-2"></i> 
              تصوير ورقة: {{ selectedSubject?.subject_name }} 
              <span class="fs-6 text-light ms-3">(الطالب: {{ selectedRequest?.student_name }})</span>
            </h5>
            <button type="button" class="btn-close btn-close-white" @click="closeScanner"></button>
          </div>
          <div class="modal-body p-0 bg-secondary">
            <!-- استدعاء مكون التصوير -->
            <MultiPageScanner 
                v-if="selectedSubject"
                :subjectId="selectedSubject.id" 
                @upload-success="handleUploadSuccess" 
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import _ from 'lodash'; 
import api from '@/api/axios';
import MultiPageScanner from './ExamPaperViewer/MultiPageScanner.vue'; // تأكد من المسار

export default {
  name: "CentralPaperMonitor",
  components: {
    MultiPageScanner
  },
  data() {
    return {
      showScannerModal: false,
      selectedSubject: null,
      selectedRequest: null,
      loading: false,
      requests: [],
      stats: { pending: 0, ready_today: 0 },
      pagination: {
          current_page: 1,
          last_page: 1,
          total: 0,
          from: 0,
          to: 0
      },
      
      filters: {
        search: "",
        subject_id: "",
        page: 1 // هام جداً للتصفح
      },
      
      polling: null
    };
  },
  
  computed: {
    // منطق عرض أرقام الصفحات
    getPageNumbers() {
        if (!this.pagination.last_page) return [];
        let current = this.pagination.current_page;
        let last = this.pagination.last_page;
        let delta = 2;
        let left = current - delta;
        let right = current + delta + 1;
        let range = [];

        for (let i = 1; i <= last; i++) {
            if (i == 1 || i == last || (i >= left && i < right)) {
                range.push(i);
            }
        }
        return range;
    }
  },

  mounted() {
    this.fetchData();
    // تحديث تلقائي كل 30 ثانية
    this.polling = setInterval(this.fetchData, 30000);
  },
  
  beforeDestroy() {
    clearInterval(this.polling);
  },
  
  methods: {
    // جلب البيانات
    async fetchData() {
      this.loading = true;
      try {
        const res = await api.get('/appeals/PaperExtraction', { params: this.filters });
        this.requests = res.data.requests.data;
        this.pagination = res.data.requests; // تخزين بيانات التصفح
        this.stats = res.data.stats;
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    // تغيير الصفحة
    changePage(page) {
        if (page < 1 || page > this.pagination.last_page) return;
        this.filters.page = page;
        this.fetchData();
    },

    // بحث سريع
    debouncedFetch: _.debounce(function() {
      this.filters.page = 1; // العودة للصفحة الأولى عند البحث
      this.fetchData();
    }, 500),

    // فتح نافذة التصوير
    openScanner(subject, request) {
      if(subject.paper_status === 'ready') {
          if(!confirm("تم رفع الورقة لهذه المادة مسبقاً. هل تريد إعادة التصوير؟")) return;
      }
      this.selectedSubject = subject;
      this.selectedRequest = request;
      this.showScannerModal = true;
    },

    closeScanner() {
      this.showScannerModal = false;
      this.selectedSubject = null;
      this.selectedRequest = null;
    },

    // عند نجاح الرفع
    handleUploadSuccess() {
      this.closeScanner();
      if (this.selectedSubject) {
          this.selectedSubject.paper_status = 'ready'; // تحديث اللون محلياً
      }
      alert("تم الحفظ بنجاح!");
      // يمكنك تحديث البيانات من السيرفر إذا أردت التأكد 100%
      // this.fetchData();
    },

    async markReady(req) {
      this.$set(req, 'processing', true);
      try {
        await api.post(`/appeals/paper-extraction/${req.id}/ready`);
        req.paper_status = 'ready';
        this.stats.pending--;
        this.stats.ready_today++;
      } catch (error) {
        alert("حدث خطأ، حاول مرة أخرى");
      } finally {
        this.$set(req, 'processing', false);
      }
    },

    formatTime(date) {
        if(!date) return '';
        return new Date(date).toLocaleString( {
            month: 'numeric', day: 'numeric', hour: '2-digit', minute:'2-digit'
        });
    }
  }
};
</script>

<style scoped>
.border-top-primary { border-top: 4px solid #0d6efd; }
.table-success-subtle { background-color: #e6f9ee !important; }

/* تأثير وميض خفيف للطلبات المعلقة */
@keyframes blink-soft {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}
.blink-soft {
  animation: blink-soft 2s infinite;
}

/* تنسيق أزرار التصفح */
.page-link {
    cursor: pointer;
    color: #333;
}
.page-item.active .page-link {
    background-color: #0d6efd;
    border-color: #0d6efd;
    color: white;
}
</style>